<template>
  <div class="warehouse-page">
    <div class="page-intro">
      <i class="fa-solid fa-clinic-medical"></i>
      <div>
        <strong>SCMS clinic dispensary stock</strong> — same clinic IDs as SECTOR branch mapping.
        <p class="intro-note">
          Dashboard <em>Clinic</em> site type uses these locations. Pull clinics from Data Flow first.
        </p>
      </div>
    </div>

    <p v-if="error" class="banner error">{{ error }}</p>
    <p v-if="notice" class="banner ok">{{ notice }}</p>

    <div class="toolbar">
      <input
        v-model="filters.search"
        type="search"
        placeholder="Search SKU, item or clinic..."
        @keyup.enter="search"
      >
      <button class="btn" type="button" @click="search" :disabled="exporting">
        <i class="fa-solid fa-magnifying-glass"></i> Search
      </button>
      <select v-model="filters.location" @change="search">
        <option value="all">All clinics</option>
        <option v-for="location in clinicLocations" :key="location" :value="location">{{ location }}</option>
      </select>
      <select v-model="filters.expiry" @change="search">
        <option value="all">All expiry</option>
        <option value="has">Has expiry</option>
        <option value="none">No expiry</option>
      </select>
      <select v-model="filters.hygiene" @change="search">
        <option value="all">All hygiene</option>
        <option v-for="bucket in hygieneBuckets" :key="bucket.key" :value="bucket.key">{{ bucket.label }}</option>
      </select>
      <select v-model="filters.stock" @change="search">
        <option value="all">All stock</option>
        <option value="instock">On-hand &gt; 0</option>
      </select>
      <button class="btn ghost" type="button" @click="reload" :disabled="exporting">Refresh</button>
      <button class="btn export" type="button" @click="exportCsv" :disabled="exporting || !total">
        <i class="fa-solid fa-file-export"></i> {{ exporting ? 'Exporting…' : 'Export CSV' }}
      </button>
    </div>

    <div class="table-card">
      <div class="table-head">
        <h2>Clinic stock</h2>
        <p>One row per medication at one clinic. Source: SCMS clinic dispensary.</p>
      </div>
      <table>
        <thead>
          <tr>
            <th class="col-no">No</th>
            <th>SKU</th>
            <th>Item</th>
            <th>UOM</th>
            <th>Clinic</th>
            <th class="col-num">On-hand</th>
            <th class="col-num">Cost</th>
            <th class="col-num">Selling</th>
            <th class="col-num">Value</th>
            <th>Expiry</th>
            <th>Hygiene</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in visibleRows" :key="row.id">
            <td class="col-no">{{ rowNumber(index) }}</td>
            <td>{{ row.sku }}</td>
            <td>{{ row.itemName }}</td>
            <td>{{ row.uom || '—' }}</td>
            <td>
              <div>{{ row.location }}</div>
              <div class="sub">{{ row.warehouseCode }}</div>
            </td>
            <td class="col-num">{{ number(row.quantity) }}</td>
            <td class="col-num">{{ money(row.costPrice) }}</td>
            <td class="col-num">{{ money(row.sellingPrice) }}</td>
            <td class="col-num">{{ money(row.inventoryValue) }}</td>
            <td>{{ row.expiryDate || '—' }}</td>
            <td>
              <span class="hygiene-pill" :class="row.hygieneBucket">{{ row.hygieneLabel }}</span>
            </td>
          </tr>
          <tr v-if="!visibleRows.length">
            <td colspan="11" class="empty">
              No clinic stock yet. Open Data Flow → SCMS – Clinic dispensary → Pull from SCMS.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pager">
      <label class="page-size">
        Rows
        <select v-model.number="pageSize">
          <option :value="10">10</option>
          <option :value="20">20</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
      </label>
      <span>{{ total }} clinic stock rows</span>
      <button type="button" :disabled="page <= 1" @click="page -= 1">Previous</button>
      <span>Page {{ page }}</span>
      <button type="button" :disabled="page * pageSize >= total" @click="page += 1">Next</button>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import warehouseApi, { downloadWarehouseCsv } from '@/utils/warehouseApi'

const error = ref('')
const notice = ref('')
const exporting = ref(false)
const page = ref(1)
const pageSize = ref(20)
const total = ref(0)
const rows = ref([])
const lookups = ref({ locations: [] })
const hygieneBuckets = [
  { key: 'expired', label: 'Expired' },
  { key: 'lt_3m', label: 'Expires < 3 M' },
  { key: 'lt_6m', label: 'Expires < 6 M' },
  { key: 'm6_12', label: 'Expires 6–12 M' },
  { key: 'm12_18', label: 'Expires 12–18 M' },
  { key: 'gt_18m', label: 'Expires > 18 M' },
  { key: 'unknown', label: 'No expiry' }
]
const filters = reactive({
  search: '',
  source: 'scms_clinic',
  group: 'clinic',
  location: 'all',
  category: 'all',
  stock: 'all',
  expiry: 'all',
  hygiene: 'all'
})

const clinicLocations = computed(() =>
  (lookups.value.locations || []).filter((name) => /clinic|klinik|medical bay/i.test(name))
)

const visibleRows = computed(() => rows.value)

function money(value) {
  return `RM ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function number(value) {
  return Number(value || 0).toLocaleString()
}

function rowNumber(index) {
  return (page.value - 1) * pageSize.value + index + 1
}

async function reload() {
  try {
    error.value = ''
    notice.value = ''
    const params = {
      ...filters,
      page: page.value,
      pageSize: pageSize.value
    }
    const { data } = await warehouseApi.get('/api/warehouse/stock', { params })
    rows.value = data.rows || []
    total.value = data.total || 0
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to load clinic stock.'
  }
}

function search() {
  if (page.value !== 1) page.value = 1
  else reload()
}

async function exportCsv() {
  try {
    exporting.value = true
    error.value = ''
    await downloadWarehouseCsv(
      '/api/warehouse/export/stock',
      { ...filters, stock: 'all' },
      'warehouse-clinic-stock.csv'
    )
    notice.value = 'CSV export downloaded.'
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to export CSV.'
  } finally {
    exporting.value = false
  }
}

watch(page, reload)
watch(pageSize, () => {
  if (page.value !== 1) page.value = 1
  else reload()
})

onMounted(async () => {
  const { data } = await warehouseApi.get('/api/warehouse/lookups')
  lookups.value = data
  await reload()
})
</script>

<style scoped>
.page-intro {
  display: flex;
  gap: 12px;
  padding: 12px 14px;
  margin-bottom: 12px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 8px;
  font-size: 0.78rem;
  color: #065f46;
  line-height: 1.45;
}
.page-intro i { margin-top: 2px; color: #059669; }
.page-intro code {
  font-size: 0.7rem;
  background: #d1fae5;
  padding: 1px 4px;
  border-radius: 4px;
}
.intro-note { margin: 6px 0 0; color: #047857; font-size: 0.72rem; }
.toolbar, .pager {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 12px;
}
.toolbar input, .toolbar select, .btn, .pager button, .page-size select {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  font-size: 0.813rem;
}
.btn { background: #0ea5e9; color: white; border-color: #0ea5e9; cursor: pointer; }
.btn.ghost { background: white; color: #475569; }
.btn.export { background: #059669; border-color: #059669; }
.btn i { margin-right: 4px; }
.btn:disabled { opacity: 0.6; cursor: not-allowed; }
.banner { padding: 10px 12px; border-radius: 6px; margin-bottom: 12px; font-size: 0.813rem; }
.banner.error { background: #fef2f2; color: #b91c1c; }
.banner.ok { background: #ecfdf5; color: #047857; }
.table-card { background: white; border: 1px solid #e2e8f0; border-radius: 8px; overflow: auto; border-top: 3px solid #059669; }
.table-head { padding: 14px 16px 8px; }
.table-head h2 { margin: 0 0 4px; font-size: 1rem; color: #0f172a; }
.table-head p { margin: 0; font-size: 0.75rem; color: #64748b; }
table { width: 100%; border-collapse: collapse; }
th, td { padding: 10px 8px; border-bottom: 1px solid #e2e8f0; font-size: 0.78rem; text-align: left; vertical-align: top; }
th { background: #f8fafc; color: #475569; }
.col-no { width: 44px; text-align: center; }
.col-num { text-align: right; white-space: nowrap; }
.sub { font-size: 0.7rem; color: #94a3b8; margin-top: 2px; }
.empty { text-align: center; color: #64748b; padding: 24px; }
.hygiene-pill {
  display: inline-flex; padding: 2px 8px; border-radius: 999px; font-size: 0.7rem; font-weight: 600; white-space: nowrap;
  background: #f1f5f9; color: #64748b;
}
.hygiene-pill.expired { background: #7f1d1d; color: #fecaca; }
.hygiene-pill.lt_3m { background: #fee2e2; color: #991b1b; }
.hygiene-pill.lt_6m { background: #ffedd5; color: #9a3412; }
.hygiene-pill.m6_12 { background: #fef9c3; color: #854d0e; }
.hygiene-pill.m12_18 { background: #ecfccb; color: #3f6212; }
.hygiene-pill.gt_18m { background: #dcfce7; color: #166534; }
.pager { justify-content: flex-end; color: #64748b; font-size: 0.813rem; }
.page-size { display: flex; align-items: center; gap: 6px; margin-right: auto; }
</style>
